/**
 * ProjectContentForm - Step 2: Project Content & Description
 */

import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Chip,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, FlashOn as FlashIcon } from '@mui/icons-material';
import { ProjectContent } from '../types';
import { CircularProgress, Tooltip } from '@mui/material';
import { API_URL } from '../config';

interface Props {
  data: ProjectContent;
  onChange: (data: ProjectContent) => void;
}

const ProjectContentForm: React.FC<Props> = ({ data, onChange }) => {
  const [formData, setFormData] = React.useState<ProjectContent>(
    data || {
      problem_statement: '',
      main_objective: '',
      target_groups: [],
      expected_results: [],
      innovation_elements: '',
      eu_priorities: []
    }
  );

  const [tempTargetGroup, setTempTargetGroup] = React.useState('');
  const [tempResult, setTempResult] = React.useState('');
  const [isGeneratingObjective, setIsGeneratingObjective] = React.useState(false);
  const [isGeneratingInnovation, setIsGeneratingInnovation] = React.useState(false);

  React.useEffect(() => {
    onChange(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleChange = (field: keyof ProjectContent, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const generateObjective = async () => {
    if (!formData.problem_statement) {
      alert('Please fill in the Problem Statement first');
      return;
    }

    setIsGeneratingObjective(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/generate-objective`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          problem_statement: formData.problem_statement,
          context: formData.problem_statement // Using problem as context for now
        })
      });

      if (!response.ok) throw new Error('Failed to generate objective');

      const result = await response.json();
      setFormData({ ...formData, main_objective: result.objective });
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsGeneratingObjective(false);
    }
  };

  const generateInnovation = async () => {
    if (!formData.problem_statement || !formData.main_objective) {
      alert('Please fill in Problem Statement and Main Objective first');
      return;
    }

    setIsGeneratingInnovation(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/generate-innovation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          problem_statement: formData.problem_statement,
          main_objective: formData.main_objective,
          context: formData.problem_statement
        })
      });

      if (!response.ok) throw new Error('Failed to generate innovation');

      const result = await response.json();
      setFormData({ ...formData, innovation_elements: result.innovation });
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsGeneratingInnovation(false);
    }
  };

  const addTargetGroup = () => {
    if (tempTargetGroup.trim()) {
      setFormData({
        ...formData,
        target_groups: [...(formData.target_groups || []), tempTargetGroup.trim()]
      });
      setTempTargetGroup('');
    }
  };

  const removeTargetGroup = (index: number) => {
    setFormData({
      ...formData,
      target_groups: formData.target_groups?.filter((_, i) => i !== index)
    });
  };

  const addResult = () => {
    if (tempResult.trim()) {
      setFormData({
        ...formData,
        expected_results: [...(formData.expected_results || []), tempResult.trim()]
      });
      setTempResult('');
    }
  };

  const removeResult = (index: number) => {
    setFormData({
      ...formData,
      expected_results: formData.expected_results?.filter((_, i) => i !== index)
    });
  };

  const euPrioritiesOptions = [
    'Inclusion and Diversity',
    'Digital Transformation',
    'Environment and Climate Change',
    'Participation in Democratic Life',
    'Common Values and Civic Engagement',
    'Green Skills',
    'Entrepreneurship',
    'Social Innovation'
  ];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
        📝 Project Content & Objectives
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'white !important' }}>
        Describe your project's context, objectives, and expected impact
      </Typography>

      {/* Problem Statement */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          🎯 Problem Statement & Context
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          label="What problem does your project address?"
          value={formData.problem_statement}
          onChange={(e) => handleChange('problem_statement', e.target.value)}
          placeholder="Describe the problem, challenge, or need that your project aims to address. Be specific about the context, affected groups, and why this issue matters at the European level."
          required
          helperText={`${formData.problem_statement?.length || 0}/2000 characters (minimum 200)`}
          InputLabelProps={{
            shrink: true
          }}
        />
      </Paper>

      {/* Main Objective */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            🎯 Main Objective
          </Typography>
          <Tooltip title="Generate objective automatically with AI from your problem statement">
            <span>
              <IconButton
                onClick={generateObjective}
                disabled={isGeneratingObjective || !formData.problem_statement}
                color="warning"
                size="large"
              >
                {isGeneratingObjective ? <CircularProgress size={24} /> : <FlashIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="What is the main objective of your project?"
          value={formData.main_objective}
          onChange={(e) => handleChange('main_objective', e.target.value)}
          placeholder="State your project's main goal. Be specific, measurable, and ambitious. Or click ⚡ to generate automatically!"
          required
          helperText={`${formData.main_objective?.length || 0}/1000 characters (minimum 100)`}
          InputLabelProps={{
            shrink: true
          }}
        />
      </Paper>

      {/* Target Groups */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          👥 Target Groups
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Who will directly benefit from your project?
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Add target group"
            value={tempTargetGroup}
            onChange={(e) => setTempTargetGroup(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addTargetGroup()}
            placeholder="e.g., Unemployed adults 45-65 years old"
          />
          <IconButton onClick={addTargetGroup} color="primary">
            <AddIcon />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {formData.target_groups?.map((group, index) => (
            <Chip
              key={index}
              label={group}
              onDelete={() => removeTargetGroup(index)}
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>
        {!formData.target_groups?.length && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No target groups added yet. Add at least 2-3 target groups.
          </Typography>
        )}
      </Paper>

      {/* Expected Results */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📦 Expected Results & Outputs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What tangible results will your project produce?
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Add expected result"
            value={tempResult}
            onChange={(e) => setTempResult(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addResult()}
            placeholder="e.g., Digital Skills Training Program for 500 adults"
          />
          <IconButton onClick={addResult} color="primary">
            <AddIcon />
          </IconButton>
        </Box>

        <Stack spacing={1}>
          {formData.expected_results?.map((result, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 1
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {index + 1}. {result}
              </Typography>
              <IconButton onClick={() => removeResult(index)} size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Stack>
        {!formData.expected_results?.length && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No results added yet. Add 3-5 concrete, measurable results.
          </Typography>
        )}
      </Paper>

      {/* Innovation Elements */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            💡 Innovation & Added Value
          </Typography>
          <Tooltip title="Generate innovation description automatically with AI from your objectives">
            <span>
              <IconButton
                onClick={generateInnovation}
                disabled={isGeneratingInnovation || !formData.problem_statement || !formData.main_objective}
                color="warning"
                size="large"
              >
                {isGeneratingInnovation ? <CircularProgress size={24} /> : <FlashIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={5}
          label="What makes your project innovative?"
          value={formData.innovation_elements}
          onChange={(e) => handleChange('innovation_elements', e.target.value)}
          placeholder="Describe what is new, original, or different about your approach. Or click ⚡ to generate automatically!"
          helperText="Explain the innovative aspects of your methodology, approach, or expected results"
          InputLabelProps={{
            shrink: true
          }}
        />
      </Paper>

      {/* EU Priorities */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          🇪🇺 EU Priorities Alignment
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Select EU Priorities</InputLabel>
          <Select
            multiple
            value={formData.eu_priorities || []}
            onChange={(e) => handleChange('eu_priorities', e.target.value)}
            input={<OutlinedInput label="Select EU Priorities" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {euPrioritiesOptions.map((priority) => (
              <MenuItem key={priority} value={priority}>
                {priority}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
        <Typography variant="body2">
          ✅ <strong>AI Tip:</strong> The more detailed and specific your descriptions, the better quality content the AI will generate!
        </Typography>
      </Box>
    </Box>
  );
};

export default ProjectContentForm;

